import { memo, useCallback, useState } from 'react';
import type { TimePickerProps } from '@mui/x-date-pickers';
import { LocalizationProvider, TimePicker as MuiTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import type { Theme } from '@mui/material';
import { FormHelperText, InputAdornment, InputLabel, styled } from '@mui/material';
import type { FormItemUiDefinition } from '../../../types/formItem.ts';
import { getIcon } from '../../../utils/common.ts';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../utils/styles.ts';

const StyledTimePicker = styled(MuiTimePicker, {
  shouldForwardProp: (prop) => prop !== '$error' && prop !== 'background',
})(({ theme, background = 'transparent', $error: error }: { theme?: Theme; background?: 'transparent' | 'white'; $error?: boolean }) => ({
  width: '100%',
  minWidth: 'auto',

  '&.MuiTextField-root .MuiOutlinedInput-root': {
    padding: '6px 10px',
    backgroundColor: error ? theme?.palette.error.light : background === 'transparent' ? 'transparent' : theme?.palette.common.white,
  },
  '&.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: theme?.palette.secondary.main,
  },
  '&.MuiTextField-root .Mui-error': {
    backgroundColor: theme?.palette.error.light,
  },
}));

interface Props extends Omit<TimePickerProps<Date>, 'value'> {
  error?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
  value: Date | null;
  formItemUiDefinition?: FormItemUiDefinition | null;
  background?: 'white' | 'transparent';
  fullWidth?: boolean;
  hideIcon?: boolean;
  dataTestId?: string;
}

export const TimePicker = memo(({ label, error, helperText, placeholder, formItemUiDefinition, hideIcon, dataTestId, ...rest }: Props) => {
  const [open, setOpen] = useState(false);
  const Icon = formItemUiDefinition?.icon ? getIcon(formItemUiDefinition?.icon) : null;

  const boxStyles = getUiDefinitionContainerStyles(formItemUiDefinition);
  const textStyles = getUiDefinitionTextStyles(formItemUiDefinition);

  const handleOpen = useCallback(() => {
    if (!rest?.disabled) {
      setOpen(true);
    }
  }, [rest?.disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div style={{ width: '100%' }}>
        <InputLabel>{label}</InputLabel>
        <StyledTimePicker
          data-testid={dataTestId}
          {...rest}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          value={rest.value ? new Date(rest.value) : null}
          closeOnSelect={false}
          ampm={false}
          format="HH:mm"
          timeSteps={{ minutes: 1 }}
          slotProps={{
            openPickerButton: {
              sx: {
                display: hideIcon ? 'none' : 'inline-flex',
              },
            },
            textField: {
              error,
              placeholder,
              sx: { ...textStyles, ...boxStyles },
              inputProps: {
                readOnly: false,
              },
              InputProps: {
                startAdornment: Icon ? (
                  <InputAdornment position="start">
                    <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />
                  </InputAdornment>
                ) : null,
              },
              onClick: (e) => e.stopPropagation(),
            },
            popper: {
              sx: {
                zIndex: 100001,
                '& .MuiPaper-root': {
                  boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                },
              },
            },
            actionBar: {
              actions: ['clear'],
            },
          }}
        />

        <FormHelperText error={error} variant="filled">
          {helperText ?? ''}
        </FormHelperText>
      </div>
    </LocalizationProvider>
  );
});
